import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ControlTower from "@/pages/ControlTower";
import ActionCenter from "@/pages/ActionCenter";
import Brands from "@/pages/Brands";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Products from "@/pages/Products";
import Customers from "@/pages/Customers";
import Workflows from "@/pages/Workflows";
import Hubs from "@/pages/Hubs";
import Inventory from "@/pages/Inventory";
import Queue from "@/pages/Queue";
import Integrations from "@/pages/Integrations";
import Clients from "@/pages/Clients";
import Tasks from "@/pages/Tasks";
import Approvals from "@/pages/Approvals";
import Production from "@/pages/Production";
import Websites from "@/pages/Websites";
import Social from "@/pages/Social";
import Shipping from "@/pages/Shipping";
import ClientWorkspace from "@/pages/ClientWorkspace";
import CalendarPage from "@/pages/CalendarPage";
import Billing from "@/pages/Billing";
import Team from "@/pages/Team";
import Workload from "@/pages/Workload";
import Vault from "@/pages/Vault";
import Portal from "@/pages/Portal";
import Roles from "@/pages/Roles";
import TaskWorkspace from "@/pages/TaskWorkspace";
import { canAccess } from "@/components/Layout";
import "@/App.css";

function AccessDenied() {
  return (
    <div className="p-16 text-center" data-testid="access-denied">
      <div className="font-display text-3xl font-bold mb-2">Access restricted</div>
      <p className="text-zinc-500 text-sm">You don't have permission to view this module. Contact your administrator if you need access.</p>
    </div>
  );
}

function Protected({ children, mod }) {
  const { user } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center bg-[#050505] text-zinc-500 font-mono text-sm">Loading command center…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const content = mod && !canAccess(user, mod) ? <AccessDenied /> : children;
  return <Layout>{content}</Layout>;
}

function AppRoutes() {
  const P = (el, mod) => <Protected mod={mod}>{el}</Protected>;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/portal/:token" element={<Portal />} />
      <Route path="/" element={P(<ControlTower />, "control_tower")} />
      <Route path="/action-center" element={P(<ActionCenter />, "action_center")} />
      <Route path="/brands" element={P(<Brands />, "brands")} />
      <Route path="/orders" element={P(<Orders />, "orders")} />
      <Route path="/orders/:id" element={P(<OrderDetail />, "orders")} />
      <Route path="/products" element={P(<Products />, "products")} />
      <Route path="/customers" element={P(<Customers />, "customers")} />
      <Route path="/hubs" element={P(<Hubs />, "hubs")} />
      <Route path="/inventory" element={P(<Inventory />, "inventory")} />
      <Route path="/queue" element={P(<Queue />, "queue")} />
      <Route path="/integrations" element={P(<Integrations />, "order_sync")} />
      <Route path="/workflows" element={P(<Workflows />, "automation")} />
      <Route path="/clients" element={P(<Clients />, "customers")} />
      <Route path="/clients/:id" element={P(<ClientWorkspace />, "customers")} />
      <Route path="/tasks" element={P(<Tasks />, "tasks")} />
      <Route path="/tasks/:id" element={P(<TaskWorkspace />, "tasks")} />
      <Route path="/approvals" element={P(<Approvals />, "approvals")} />
      <Route path="/production" element={P(<Production />, "production")} />
      <Route path="/websites" element={P(<Websites />, "websites")} />
      <Route path="/social" element={P(<Social />, "social")} />
      <Route path="/shipping" element={P(<Shipping />, "shipping")} />
      <Route path="/calendar" element={P(<CalendarPage />, "calendar")} />
      <Route path="/billing" element={P(<Billing />, "billing")} />
      <Route path="/team" element={P(<Team />, "team")} />
      <Route path="/roles" element={P(<Roles />, "team")} />
      <Route path="/workload" element={P(<Workload />, "workload")} />
      <Route path="/vault" element={P(<Vault />, "vault")} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrandProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </BrandProvider>
        <Toaster position="top-right" theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
